"use client"

import { memo, useEffect, useState } from "react"
import { useToast } from "@/components/ui/use-toast"
import { axiosInstance } from "@/lib/axios"
import { useRouter, usePathname } from "next/navigation"
import { Toaster } from "@/components/ui/toaster"
import { cn } from "@/lib/utils"
import dynamic from 'next/dynamic'
import Script from 'next/script'
import DiscountTracker from '../DiscountTracker'
import DisableDevTools from '../security/DisableDevTools'
// Lazy load non-critical components with loading states
const Header = dynamic(() => import('./Header'), {
	loading: () => <div className="h-16 w-full animate-pulse bg-gray-100" aria-hidden="true" />
})

const Footer = dynamic(() => import('./Footer'), {
	loading: () => <div className="h-16 w-full animate-pulse bg-gray-100" aria-hidden="true" />,
	ssr: true
})

const Sidebar = dynamic(() => import('./Sidebar'), {
	loading: () => <div className="w-[var(--sidebar-width)] animate-pulse bg-gray-100" aria-hidden="true" />,
	ssr: true
})

const ReferralTracker = dynamic(() => import('../ReferralTracker'), {
	ssr: false
})

// Add CSS variables for layout dimensions
const layoutStyles = `
	:root {
		--header-height: 3rem;
		--footer-height: 3rem;
		--sidebar-width: 16rem;
	}
`

// Memoized layout component
const RootLayoutClient = memo(function RootLayoutClient({
	children,
}: {
	children: React.ReactNode
}) {
	const [isHydrated, setIsHydrated] = useState(false)
	const pathname = usePathname()

	// Check if we're in the appsheet route - update to only match /appsheet/* paths
	const isAppSheet = pathname?.startsWith('/appsheet/')

	// Handle hydration
	useEffect(() => {
		setIsHydrated(true)
	}, [])

	// Wait for hydration to complete
	if (!isHydrated) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div
					className="h-32 w-32 animate-spin rounded-full border-gray-900 border-b-2"
					role="progressbar"
					aria-label="Loading"
				/>
			</div>
		)
	}

	// Return only children for appsheet routes
	if (isAppSheet) {
		return (
			<>
				{children}
				<DisableDevTools />
			</>
		)
	}
	const isDev = process.env.NODE_ENV === "development"

	return (
		<>
			{/* Add CSS variables */}
			<style>{layoutStyles}</style>

			{/* Load GA script with low priority */}
			{!isDev && (
				<>
					<Script
						src="https://www.googletagmanager.com/gtag/js?id=G-QPN2EGVDHR"
						strategy="lazyOnload"
						onError={(e) => {
							// Silently handle GA blocking by ad blockers
							console.info('Analytics blocked by browser extension or privacy settings')
						}}
					/>
					<Script id="google-analytics" strategy="lazyOnload">
						{`
						window.dataLayer = window.dataLayer || [];
						function gtag(){dataLayer.push(arguments);}
						gtag('js', new Date());
						gtag('config', 'G-QPN2EGVDHR', {
							page_path: window.location.pathname,
							siteSpeedSampleRate: 100
						});
					`}
					</Script>
				</>
			)}

			<div className="relative flex min-h-screen flex-col">
				<ReferralTracker />
				<DiscountTracker />
				<Header />
				<div className="relative flex flex-1">
					{/* Sidebar for screens smaller than xl */}
					<div
						className="fixed top-[var(--header-height)] bottom-[var(--footer-height)] z-20 w-[var(--sidebar-width)] xl:hidden"
						style={{ contain: 'size layout' }}
					>
						<Sidebar />
					</div>
					<main
						className={cn(
							"relative flex-1 overflow-y-auto overflow-x-hidden",
							"xl:ml-0"
						)}
					>
						<div className="container mx-auto">
							{children}
						</div>
					</main>
				</div>
				<div className="bottom-0 z-30 h-[var(--footer-height)] w-full bg-background">
					<Footer />
				</div>
				<Toaster />
			</div>
		</>
	)
})

RootLayoutClient.displayName = "RootLayoutClient"

export default RootLayoutClient 